import { LinkOutlined, UserOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { Link } from '@umijs/max';
import React from 'react';
import { AvatarDropdown, AvatarName, EnvironmentPanel, EnvironmentProvider, EnvironmentStatus, Footer, SelectLang, ThemeSelector } from '@/components';
import { history } from '@umijs/max';
import { loadIamClaims } from '@/services/iam/oidc';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

// 主题配置类型
type ThemeMode = 'light' | 'dark' | 'darkOrange' | 'minimal' | 'auto';

// 从localStorage获取主题配置
const getThemeFromStorage = (): ThemeMode => {
  if (typeof window === 'undefined') return 'auto';
  return (localStorage.getItem('er-console-theme') as ThemeMode) || 'auto';
};

// 获取主题token配置
const getThemeTokenConfig = (mode: ThemeMode) => {
  switch (mode) {
    case 'darkOrange':
      return {
        colorPrimary: '#ff7a00',
        colorPrimaryHover: '#ff9533',
        colorPrimaryActive: '#e66a00',
        colorBgContainer: '#141414',
        colorBgElevated: '#1f1f1f',
        colorText: '#ffffff',
        colorTextSecondary: '#cccccc',
        colorBorder: '#434343',
        colorBorderSecondary: '#303030',
      };
    case 'minimal':
      return {
        colorPrimary: '#000000',
        colorPrimaryHover: '#262626',
        colorPrimaryActive: '#000000',
      };
    default:
      return {
        fontFamily: 'AlibabaSans, sans-serif',
      };
  }
};

const isDev = process.env.NODE_ENV === 'development';

/**
 * 登录已接入 IAM（OIDC 标准授权码 + PKCE）：
 * getInitialState 启动时向 IAM 取身份；无会话时整体跳转 IAM 授权页，
 * IAM 有 SSO 会话时静默完成。回跳路径 /callback 见 src/pages/callback。
 */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async (): Promise<API.CurrentUser | undefined> => {
    const claims = loadIamClaims();
    if (!claims) return undefined;
    return {
      name: claims.name ?? claims.preferred_username ?? claims.email ?? claims.sub,
      userid: claims.sub,
      email: claims.email,
      access: 'admin',
    };
  };
  const currentUser = await fetchUserInfo();
  return {
    fetchUserInfo,
    currentUser,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    actionsRender: () => [<ThemeSelector key="ThemeSelector" />, <SelectLang key="SelectLang" />],
    avatarProps: {
      // 无头像体系：统一使用图标库头像
      icon: <UserOutlined />,
      style: { backgroundColor: '#1677ff', color: '#fff' },
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    // 侧边栏左下角：当前 EnderRealm 服务器环境（红绿灯 + 名称），点击打开配置面板
    menuFooterRender: (props) => <EnvironmentStatus collapsed={props?.collapsed} />,
    onPageChange: (location) => {
      // 未登录访问受保护页面 → 引导到登录页（登录页/回调页自身除外）
      const path = location?.pathname ?? window.location.pathname;
      if (!initialState?.currentUser && path !== '/login' && path !== '/callback') {
        history.replace('/login');
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name rootContainer 根容器
 * @description 用 EnvironmentProvider 包住整个应用（含侧边栏），环境状态全局共享
 */
export function rootContainer(container: React.ReactElement) {
  return (
    <EnvironmentProvider>
      {container}
      <EnvironmentPanel />
    </EnvironmentProvider>
  );
}

/**
 * @name request 配置，可以配置错误处理
 * @description 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  // 临时注释掉远程 baseURL，让请求走本地 mock 假数据；
  // 联调真实后端时再放开，并在 config/proxy.ts 里配置代理
  // baseURL: 'https://proapi.azurewebsites.net',
  ...errorConfig,
};

/**
 * @name antd 运行时配置
 * @description 动态配置 Ant Design 主题
 * @doc https://umijs.org/docs/max/antd#运行时配置
 */
export const antd = () => {
  const currentTheme = getThemeFromStorage();
  const tokenConfig = getThemeTokenConfig(currentTheme);
  
  return {
    configProvider: {
      theme: {
        cssVar: true,
        token: tokenConfig,
      },
    },
  };
};
